import type { BadgeProps } from '@/components/ui/Badge';
import { canWrite, type Role } from '@/lib/permissions';

type BadgeVariant = NonNullable<BadgeProps['variant']>;

// ---------------------------------------------------------------------------
// Storage
// ---------------------------------------------------------------------------

export const ENG_FILES_BUCKET = 'eng-files';
/** Matches the bucket's file_size_limit in migration 142. */
export const MAX_ENG_FILE_BYTES = 500 * 1024 * 1024;
export const MAX_ENG_PHOTO_BYTES = 25 * 1024 * 1024;

// ---------------------------------------------------------------------------
// Project status — must match eng_projects_status_check (migration 142).
// ---------------------------------------------------------------------------

export const ENG_STATUSES = [
  'concept',
  'design',
  'prototype',
  'testing',
  'design_locked',
  'production',
  'on_hold',
  'cancelled',
] as const;
export type EngStatus = (typeof ENG_STATUSES)[number];

export interface EngStatusMeta {
  label: string;
  shortLabel: string;
  variant: BadgeVariant;
  hint: string;
  /** Files, folders and versions are read-only in locked states. */
  locked: boolean;
  /** Dashboard grouping / sort order. */
  order: number;
}

export const ENG_STATUS_META: Record<EngStatus, EngStatusMeta> = {
  concept: {
    label: 'Concept',
    shortLabel: 'Concept',
    variant: 'muted',
    hint: 'Idea stage — requirements, sketches, research.',
    locked: false,
    order: 0,
  },
  design: {
    label: 'In design',
    shortLabel: 'Design',
    variant: 'info-soft',
    hint: 'CAD and drawings are being worked on.',
    locked: false,
    order: 1,
  },
  prototype: {
    label: 'Prototyping',
    shortLabel: 'Prototype',
    variant: 'info',
    hint: 'Parts are being printed / machined and fitted.',
    locked: false,
    order: 2,
  },
  testing: {
    label: 'Testing',
    shortLabel: 'Testing',
    variant: 'warning-soft',
    hint: 'Prototypes are under evaluation.',
    locked: false,
    order: 3,
  },
  design_locked: {
    label: 'Design locked',
    shortLabel: 'Locked',
    variant: 'success-soft',
    hint: 'Design is frozen. Files are read-only until the project is reopened.',
    locked: true,
    order: 4,
  },
  production: {
    label: 'In production',
    shortLabel: 'Production',
    variant: 'success',
    hint: 'Released to manufacturing. Files stay read-only.',
    locked: true,
    order: 5,
  },
  on_hold: {
    label: 'On hold',
    shortLabel: 'On hold',
    variant: 'warning',
    hint: 'Paused — nothing is being worked on right now.',
    locked: false,
    order: 6,
  },
  cancelled: {
    label: 'Cancelled',
    shortLabel: 'Cancelled',
    variant: 'muted',
    hint: 'The project was dropped.',
    locked: false,
    order: 7,
  },
};

/** The main development pipeline, in order, for the dashboard funnel. */
export const ENG_PIPELINE_STATUSES: EngStatus[] = [
  'concept',
  'design',
  'prototype',
  'testing',
  'design_locked',
  'production',
];

export const ENG_STATUS_OPTIONS = ENG_STATUSES.map((value) => ({
  value,
  label: ENG_STATUS_META[value].label,
  hint: ENG_STATUS_META[value].hint,
}));

export function isEngStatus(value: string | null | undefined): value is EngStatus {
  return !!value && (ENG_STATUSES as readonly string[]).includes(value);
}

export function getEngStatusMeta(status: string): EngStatusMeta {
  return isEngStatus(status)
    ? ENG_STATUS_META[status]
    : { label: humanize(status), shortLabel: humanize(status), variant: 'muted', hint: '', locked: false, order: 99 };
}

export function isEngLockedStatus(status: string): boolean {
  return isEngStatus(status) && ENG_STATUS_META[status].locked;
}

// ---------------------------------------------------------------------------
// Team roles — eng_project_members_role_check
// ---------------------------------------------------------------------------

export const ENG_MEMBER_ROLES = ['lead', 'engineer', 'designer', 'technician', 'stakeholder'] as const;
export type EngMemberRole = (typeof ENG_MEMBER_ROLES)[number];

export const ENG_MEMBER_ROLE_LABELS: Record<EngMemberRole, string> = {
  lead: 'Lead',
  engineer: 'Engineer',
  designer: 'Designer',
  technician: 'Technician',
  stakeholder: 'Stakeholder',
};

export const ENG_MEMBER_ROLE_OPTIONS = ENG_MEMBER_ROLES.map((value) => ({
  value,
  label: ENG_MEMBER_ROLE_LABELS[value],
}));

// ---------------------------------------------------------------------------
// Files — categories derived from the extension (eng_files_category_check)
// ---------------------------------------------------------------------------

export const ENG_FILE_CATEGORIES = [
  'cad',
  'mesh',
  'drawing',
  'image',
  'document',
  'spreadsheet',
  'archive',
  'other',
] as const;
export type EngFileCategory = (typeof ENG_FILE_CATEGORIES)[number];

export const ENG_FILE_CATEGORY_LABELS: Record<EngFileCategory, string> = {
  cad: 'CAD model',
  mesh: '3D mesh',
  drawing: 'Drawing',
  image: 'Image',
  document: 'Document',
  spreadsheet: 'Spreadsheet',
  archive: 'Archive',
  other: 'Other',
};

const CAD_EXTENSIONS = new Set([
  'step', 'stp', 'iges', 'igs', 'brep', 'sldprt', 'sldasm', 'slddrw', 'f3d', 'f3z', 'ipt', 'iam',
  'prt', 'asm', 'x_t', 'x_b', 'sat', 'catpart', 'catproduct', 'scdoc', 'skp', '3dm', 'par', 'psm',
]);
const MESH_EXTENSIONS = new Set(['stl', 'obj', '3mf', 'glb', 'gltf', 'ply', 'amf', 'fbx', 'dae']);
const DRAWING_EXTENSIONS = new Set(['dwg', 'dxf', 'svg', 'ai', 'eps']);
const IMAGE_EXTENSIONS = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp', 'heic', 'bmp', 'tif', 'tiff']);
const DOCUMENT_EXTENSIONS = new Set(['pdf', 'doc', 'docx', 'txt', 'md', 'rtf', 'odt', 'pages', 'ppt', 'pptx', 'key']);
const SPREADSHEET_EXTENSIONS = new Set(['xls', 'xlsx', 'csv', 'numbers', 'ods']);
const ARCHIVE_EXTENSIONS = new Set(['zip', '7z', 'rar', 'tar', 'gz']);

/** Formats the in-app 3D viewer can open. */
export const VIEWABLE_3D_EXTENSIONS = new Set(['stl', 'obj', '3mf', 'glb', 'gltf', 'step', 'stp', 'iges', 'igs', 'brep']);
/** Formats that need the OpenCascade (WASM) importer rather than a three.js loader. */
export const CAD_KERNEL_EXTENSIONS = new Set(['step', 'stp', 'iges', 'igs', 'brep']);

export function fileExtension(name: string): string {
  const idx = name.lastIndexOf('.');
  if (idx < 0 || idx === name.length - 1) return '';
  return name.slice(idx + 1).toLowerCase();
}

export function categoryForFileName(name: string): EngFileCategory {
  const ext = fileExtension(name);
  if (CAD_EXTENSIONS.has(ext)) return 'cad';
  if (MESH_EXTENSIONS.has(ext)) return 'mesh';
  if (DRAWING_EXTENSIONS.has(ext)) return 'drawing';
  if (IMAGE_EXTENSIONS.has(ext)) return 'image';
  if (DOCUMENT_EXTENSIONS.has(ext)) return 'document';
  if (SPREADSHEET_EXTENSIONS.has(ext)) return 'spreadsheet';
  if (ARCHIVE_EXTENSIONS.has(ext)) return 'archive';
  return 'other';
}

export function isViewable3D(name: string): boolean {
  return VIEWABLE_3D_EXTENSIONS.has(fileExtension(name));
}

export function isImageFile(name: string): boolean {
  return IMAGE_EXTENSIONS.has(fileExtension(name));
}

// ---------------------------------------------------------------------------
// 3D prints — eng_prints_status_check
// ---------------------------------------------------------------------------

export const ENG_PRINT_STATUSES = [
  'planned',
  'printing',
  'success',
  'needs_rework',
  'failed',
  'cancelled',
] as const;
export type EngPrintStatus = (typeof ENG_PRINT_STATUSES)[number];

export const ENG_PRINT_STATUS_META: Record<EngPrintStatus, { label: string; variant: BadgeVariant }> = {
  planned: { label: 'Planned', variant: 'muted' },
  printing: { label: 'Printing', variant: 'info-soft' },
  success: { label: 'Success', variant: 'success-soft' },
  needs_rework: { label: 'Needs rework', variant: 'warning-soft' },
  failed: { label: 'Failed', variant: 'warning' },
  cancelled: { label: 'Cancelled', variant: 'muted' },
};

export function getEngPrintStatusMeta(status: string): { label: string; variant: BadgeVariant } {
  return (
    ENG_PRINT_STATUS_META[status as EngPrintStatus] ?? { label: humanize(status), variant: 'muted' }
  );
}

export const ENG_PRINT_STATUS_OPTIONS = ENG_PRINT_STATUSES.map((value) => ({
  value,
  label: ENG_PRINT_STATUS_META[value].label,
}));

export const PRINT_MATERIAL_SUGGESTIONS = [
  'PLA',
  'PLA+',
  'PETG',
  'ABS',
  'ASA',
  'TPU',
  'Nylon',
  'PC',
  'Resin (standard)',
  'Resin (tough)',
  'Resin (flexible)',
];

// ---------------------------------------------------------------------------
// Costs — eng_costs_category_check
// ---------------------------------------------------------------------------

export const ENG_COST_CATEGORIES = [
  'hardware',
  'materials',
  '3d_print',
  'tooling',
  'outsourced',
  'software',
  'labor',
  'shipping',
  'other',
] as const;
export type EngCostCategory = (typeof ENG_COST_CATEGORIES)[number];

export const ENG_COST_CATEGORY_LABELS: Record<EngCostCategory, string> = {
  hardware: 'Hardware purchased',
  materials: 'Materials',
  '3d_print': '3D print',
  tooling: 'Tooling / molds',
  outsourced: 'Outsourced work',
  software: 'Software',
  labor: 'Labor',
  shipping: 'Shipping',
  other: 'Other',
};

export const ENG_COST_CATEGORY_OPTIONS = ENG_COST_CATEGORIES.map((value) => ({
  value,
  label: ENG_COST_CATEGORY_LABELS[value],
}));

export function engCostCategoryLabel(value: string): string {
  return ENG_COST_CATEGORY_LABELS[value as EngCostCategory] ?? humanize(value);
}

// ---------------------------------------------------------------------------
// Item links — eng_project_items_relationship_check
// ---------------------------------------------------------------------------

export const ENG_ITEM_RELATIONSHIPS = ['designs', 'uses', 'replaces'] as const;
export type EngItemRelationship = (typeof ENG_ITEM_RELATIONSHIPS)[number];

export const ENG_ITEM_RELATIONSHIP_META: Record<
  EngItemRelationship,
  { label: string; hint: string; variant: BadgeVariant }
> = {
  designs: {
    label: 'Engineered here',
    hint: 'This project designs the item.',
    variant: 'success-soft',
  },
  uses: {
    label: 'Used in design',
    hint: 'An off-the-shelf part the design depends on.',
    variant: 'outline',
  },
  replaces: {
    label: 'Replaces',
    hint: 'The item this design supersedes.',
    variant: 'warning-soft',
  },
};

export const ENG_ITEM_RELATIONSHIP_OPTIONS = ENG_ITEM_RELATIONSHIPS.map((value) => ({
  value,
  label: ENG_ITEM_RELATIONSHIP_META[value].label,
  hint: ENG_ITEM_RELATIONSHIP_META[value].hint,
}));

// ---------------------------------------------------------------------------
// Permissions — mirror the RLS in migration 142.
// ---------------------------------------------------------------------------

export interface EngPermissionContext {
  role: Role | null;
  profileId: string | null | undefined;
  project: { owner_id: string | null; created_by: string | null } | null | undefined;
  memberIds: readonly string[];
}

/** Anyone with the Engineering section can start a project. */
export function canCreateEngProject(role: Role | null): boolean {
  return canWrite(role, 'engineering');
}

/** Owner / creator / admin / super_admin: members, archive, delete. */
export function canManageEngProject(ctx: EngPermissionContext): boolean {
  if (!ctx.role || !ctx.project || !canWrite(ctx.role, 'engineering')) return false;
  if (ctx.role === 'super_admin' || ctx.role === 'admin') return true;
  if (!ctx.profileId) return false;
  return ctx.project.owner_id === ctx.profileId || ctx.project.created_by === ctx.profileId;
}

/** The project team: files, prints, costs, notes, status. */
export function canEditEngProject(ctx: EngPermissionContext): boolean {
  if (canManageEngProject(ctx)) return true;
  if (!ctx.role || !ctx.profileId || !canWrite(ctx.role, 'engineering')) return false;
  return ctx.memberIds.includes(ctx.profileId);
}

// ---------------------------------------------------------------------------
// Misc helpers
// ---------------------------------------------------------------------------

export const ENG_CODE_PREFIX = 'ENG';

/** Next free project code: one past the highest `ENG-n` in use. */
export function suggestEngCode(existingCodes: string[]): string {
  const pattern = new RegExp(`^${ENG_CODE_PREFIX}-(\\d+)$`, 'i');
  let max = 0;
  for (const code of existingCodes) {
    const match = pattern.exec(code.trim());
    if (match) max = Math.max(max, Number(match[1]));
  }
  return `${ENG_CODE_PREFIX}-${String(max + 1).padStart(3, '0')}`;
}

export function formatBytes(bytes: number | null | undefined): string {
  if (bytes == null) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

export function formatPrintTime(minutes: number | null | undefined): string {
  if (minutes == null) return '—';
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  if (h === 0) return `${m} min`;
  if (m === 0) return `${h} h`;
  return `${h} h ${m} min`;
}

/** "2h 30m", "150", "2.5h", "90 min" → minutes. Empty → null. Throws when unparseable. */
export function parsePrintTime(input: string): number | null {
  const raw = input.trim().toLowerCase();
  if (!raw) return null;
  if (/^\d+(\.\d+)?$/.test(raw)) return Math.round(Number(raw));
  const hm = raw.match(/^(\d+(?:\.\d+)?)\s*h(?:ours?|rs?)?\s*(?:(\d+)\s*m(?:in(?:utes?)?)?)?$/);
  if (hm) return Math.round(Number(hm[1]) * 60 + Number(hm[2] ?? 0));
  const colon = raw.match(/^(\d+):(\d{1,2})$/);
  if (colon) return Number(colon[1]) * 60 + Number(colon[2]);
  const mins = raw.match(/^(\d+(?:\.\d+)?)\s*m(?:in(?:utes?)?)?$/);
  if (mins) return Math.round(Number(mins[1]));
  throw new Error(`“${input}” isn't a print time I understand. Try “2h 30m” or “150”.`);
}

export function humanize(value: string): string {
  const words = value.replace(/_/g, ' ').trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : 'Unknown';
}

/** Strip characters Supabase Storage rejects in object keys. */
export function safeStorageName(filename: string): string {
  return filename.replace(/[^a-zA-Z0-9._-]+/g, '_');
}
